/**
 * Immobilienzeilen ohne Aussage (D-084-Nachtrag, Prompt 128 §1).
 *
 * Für Gesellschaften OHNE Immobilienbestand (nicht in pph["members"], Flag
 * `portfolio_mitglied === false` aus /api/asset) tragen einige BWA-Zeilen
 * zwangsläufig nur Null — Gewerbeenergien liefert z. B. `nkm = 0.0` (NICHT null).
 * Eine dargestellte „0 €" ist dort eine Aussage ohne Inhalt (D-075).
 *
 * Regel: eine Zeile aus IMMO_KEYS entfällt NUR, wenn sie in KEINEM BWA-Block der
 * Gesellschaft einen Wert ≠ 0 trägt — Vorjahr, Jahr (Ist/Prognose/Soll), jedes
 * Quartal (Ist/Soll) und die Kumulierung — UND mindestens einmal eine echte 0.
 * Eine Zeile aus lauter `null` bleibt stehen („—"): fehlende Daten sind keine 0
 * und dürfen nicht als „durchgehend 0" ausgewiesen werden (Review-Befund 5,
 * SCHEMA_MAPPING §5). Ein einziger Wert ≠ 0 hält die Zeile (Curare → NKM bleibt).
 *
 * Für Portfolio-Mitglieder und bei fehlendem Flag wird NIE etwas ausgeblendet.
 */
import type { BwaBlock, BwaKey, Entity } from '../types/snapshot';

/** Immobilienspezifische Zeilen laut Prompt 128 §1 (Erlöse NK gehört nicht dazu). */
export const IMMO_KEYS: readonly BwaKey[] = [
  'nkm',
  'umlegb_nk',
  'nicht_umlegb_nk',
  'instandhaltung',
  'wb_mietford',
];

function istWert(v: number | null | undefined): boolean {
  return typeof v === 'number' && !Number.isNaN(v) && v !== 0;
}

function istNull(v: number | null | undefined): boolean {
  return v === 0;
}

/** Nur-Null-Reihe: mindestens eine echte 0, kein Wert ≠ 0 (null wird ignoriert). */
function nurNull(werte: (number | null | undefined)[]): boolean {
  return werte.some(istNull) && !werte.some(istWert);
}

function bloecke(e: Entity): (BwaBlock | undefined)[] {
  const q = Object.values(e.quarterly ?? {}).flatMap((qu) => (qu ? [qu.ist, qu.soll] : []));
  const kum = e.quarterly_kumuliert;
  return [
    e.vorjahr_2025,
    e.jahr_2026?.ist,
    e.jahr_2026?.prognose,
    e.jahr_2026?.soll,
    ...q,
    kum?.ist,
    kum?.soll,
  ];
}

function nichtMitglied(e: Entity): boolean {
  return e.portfolio_mitglied === false;
}

export function ausgeblendeteImmoZeilen(e: Entity): ReadonlySet<BwaKey> {
  if (!nichtMitglied(e)) return new Set();
  const bl = bloecke(e);
  return new Set(IMMO_KEYS.filter((k) => nurNull(bl.map((b) => b?.[k]))));
}

/**
 * Aktivierungen-Sektion entfällt für Nicht-Mitglieder, wenn die Konten nur Nullen
 * tragen (mindestens eine echte 0, kein Wert ≠ 0). Ohne Konten / ohne Feld rendert
 * die Komponente ohnehin nichts — das ist dann KEIN „0 ausgeblendet" (Befund 5).
 */
export function aktivierungenAusblenden(e: Entity): boolean {
  if (!nichtMitglied(e)) return false;
  const werte = (e.aktivierungen?.konten ?? []).flatMap((k) => [
    k.ytd_anfang,
    k.summe_ist,
    ...Object.values(k.quartale ?? {}).map((q) => q?.wert),
  ]);
  return nurNull(werte);
}
